/**
 * Re-arms the Sales Genius price injection after the theme re-renders a price.
 *
 * The app replaces the content of every `[data-sg-key]` wrapper with its own `<syde-price>`, driven by
 * a MutationObserver that looks for *inserted* `[data-sg-key]` elements. Anything that rewrites a
 * wrapper's children without re-inserting the wrapper itself therefore puts the undiscounted,
 * server-rendered price back for good — the injected markup carries no `data-sg-key` of its own, so the
 * app never sees it go. Two paths in this theme do exactly that:
 *
 *  - emptying the cart: `component-cart-items.js` clones `#empty-cart-template` (server HTML, so
 *    undiscounted), the app injects into the clone, then the cart response morphs the section and wipes
 *    the injection — `cart:update` is dispatched *before* that morph, so listening to it is too early;
 *  - any other morph re-render over a price, the collection filters being the other case.
 *
 * Hence reacting to the state rather than to an event: whenever a wrapper is left without its
 * `<syde-price>`, re-insert it so the app's observer fires again. `data-jd-sg-rearmed` bounds this to
 * one attempt per wrapper, which is what makes it loop-proof — our own re-insertion re-triggers the
 * observer below, and the mark makes that pass a no-op. A later morph drops the mark along with the
 * rest of the client-side state, which is precisely when the wrapper should become eligible again.
 *
 * The app's loader is module-scoped, not exposed on `window`, so a DOM nudge is the only way in.
 */
const SG_PRICE_HOOK = '[data-sg-key]';
const REARMED_ATTRIBUTE = 'data-jd-sg-rearmed';

function rearmSalesGeniusPrices() {
  for (const hook of document.querySelectorAll(`${SG_PRICE_HOOK}:not([${REARMED_ATTRIBUTE}])`)) {
    if (hook.querySelector('syde-price')) continue;

    hook.setAttribute(REARMED_ATTRIBUTE, '');
    // The app skips anything it has already stamped; a morphed-over price is not actually injected.
    hook.removeAttribute('data-sg-processed');
    hook.parentElement?.insertBefore(hook, hook.nextSibling);
  }
}

let rearmScheduled = false;

function scheduleRearm() {
  if (rearmScheduled) return;
  rearmScheduled = true;

  requestAnimationFrame(() => {
    rearmScheduled = false;
    rearmSalesGeniusPrices();
  });
}

new MutationObserver(scheduleRearm).observe(document.body, { childList: true, subtree: true });
scheduleRearm();
